import { zodResolver } from '@hookform/resolvers/zod';
import * as m from '@staiter/i18n';
import { formatDateTime } from '@staiter/i18n/format';
import { LoginRequest, type LoginRequest as LoginRequestType } from '@staiter/shared';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LogoutReasonNotice } from '~/components/auth/LogoutReasonNotice';
import { Button } from '~/components/primitives/Button';
import { Checkbox } from '~/components/primitives/Checkbox';
import { Input } from '~/components/primitives/Input';
import { login, verifyLogin2fa } from '~/lib/api/auth';
import { ApiRequestError, takeLogoutReason } from '~/lib/api/client';
import { useLocale } from '~/lib/hooks/useLocale';
import { DEFAULT_LANDING_ROUTE } from '~/lib/rbac';
import { useAuthStore } from '~/store/auth';
import styles from './login.module.css';

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    // Уже залогинен — сразу на «Задачи» (каноническая посадка, без прыжка через дашборд).
    // TODO(S5): проверять валидность токена через /me, а не только наличие.
    const hasToken =
      typeof localStorage !== 'undefined' && localStorage.getItem('staiter.access_token');
    if (hasToken && !useAuthStore.getState().mustReset) {
      throw redirect({ to: DEFAULT_LANDING_ROUTE as '/' });
    }
  },
  component: LoginPage,
});

// FE-162 Sprint Q+: simple "Remember email" — сохраняет email в localStorage
// для prefill при следующем входе. Refresh token уже handles persistent session.
const REMEMBERED_EMAIL_KEY = 'staiter.remembered_email';
function getRememberedEmail(): string {
  try {
    return localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}
function setRememberedEmail(email: string): void {
  try {
    if (email) localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
    else localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  } catch {
    /* ignore SecurityError in private mode */
  }
}

function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const setFromLogin = useAuthStore((s) => s.setFromLogin);
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number | null>(null);
  /*
   * С-I3 Р.1, круг 5 гейта (UI и UX, MAJOR): срок лимита — неизменной фразой «вход откроется в 14:05»
   * (время считается один раз), а не тикающим счётчиком в живой области: диктор читал бы его каждую
   * секунду. Отдельный скрытый регион объявляет начало блокировки и её конец.
   */
  const locale = useLocale();
  const [rateUnlockAt, setRateUnlockAt] = useState<string | null>(null);
  const [rateAnnounce, setRateAnnounce] = useState('');
  const rateText = (unlockAt: string): string => {
    const time = formatDateTime(unlockAt, locale, {
      year: undefined,
      month: undefined,
      day: undefined,
    });
    return logoutReason
      ? m.login_rate_limit_until_access({ time })
      : m.login_rate_limit_until({ time });
  };
  const wasLocked = useRef(false);
  useEffect(() => {
    if (rateLimitSeconds !== null) {
      wasLocked.current = true;
      return;
    }
    if (wasLocked.current) {
      wasLocked.current = false;
      setRateUnlockAt(null);
      setRateAnnounce(m.login_rate_limit_over());
    }
  }, [rateLimitSeconds]);
  const [serverError, setServerError] = useState<string | null>(null);
  const rememberedEmail = getRememberedEmail();
  const [rememberMe, setRememberMe] = useState(!!rememberedEmail);
  // SET-AUDIT-4: 2FA challenge step state.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code2fa, setCode2fa] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [twofaPending, setTwofaPending] = useState(false);
  // С-I3 Р.1 — причина выхода (доступ отключён администратором), читается один раз.
  const [logoutReason, setLogoutReason] = useState(takeLogoutReason);
  // Круг 3 гейта (UX, MINOR): повторный отказ перемонтирует оговорку — диктор объявит её снова.
  const [noticeKey, setNoticeKey] = useState(0);
  const showAccessDisabled = (err: ApiRequestError): void => {
    const why = (err.details as { reason?: unknown } | undefined)?.reason;
    setLogoutReason({
      reason: why === 'account_disabled' ? 'account_disabled' : 'access_revoked',
      entity_name: null,
    });
    setNoticeKey((k) => k + 1);
  };
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequestType>({
    resolver: zodResolver(LoginRequest),
    defaultValues: { email: rememberedEmail, password: '' },
  });

  const onSubmit = async (values: LoginRequestType): Promise<void> => {
    setServerError(null);
    // FE-162: persist email choice по чекбоксу.
    setRememberedEmail(rememberMe ? values.email : '');
    try {
      const resp = await login(values);
      if (resp.kind === '2fa') {
        // Круг 4 гейта Р.1 (UI и UX, MINOR): запрос кода выдан после проверки компаний — прежняя
        // оговорка «доступ отключён» над полем кода уже неверна.
        setLogoutReason(null);
        setChallenge(resp.challenge_token);
        return;
      }
      setFromLogin(resp.data);
      if (resp.data.must_reset) {
        await navigate({ to: '/force-password-change' });
      } else {
        await navigate({ to: DEFAULT_LANDING_ROUTE as '/' });
      }
    } catch (err) {
      if (err instanceof ApiRequestError) {
        // С-I3 Р.1, живая проверка круга 4 гейта: лимитер кладёт срок в `details.retry_after_seconds`
        // (и заголовок Retry-After), а не в `retry_after` конверта — блок «подождите» не появлялся вовсе,
        // и форма писала общее «Не удалось войти».
        const retryAfter =
          err.retry_after ??
          (typeof err.details?.['retry_after_seconds'] === 'number'
            ? (err.details['retry_after_seconds'] as number)
            : undefined);
        if (err.status === 429 && retryAfter) {
          const unlockAt = new Date(Date.now() + retryAfter * 1000).toISOString();
          setRateUnlockAt(unlockAt);
          setRateAnnounce(rateText(unlockAt));
          setRateLimitSeconds(retryAfter);
          startCountdown(retryAfter, setRateLimitSeconds);
          return;
        }
        // С-I3 Р.1, круг 2 гейта (UX, MAJOR): пароль верный, но доступ отключён — та же оговорка,
        // что после выхода, а не «неверный пароль» (петля через «Забыли пароль?»).
        if (err.code === 'auth.access_disabled') {
          showAccessDisabled(err);
          return;
        }
        // Круг 3 гейта (UI и UX, MINOR): другой исход — прежняя оговорка не висит рядом с новой ошибкой.
        setLogoutReason(null);
        setServerError(mapLoginError(err.code, err.message));
        return;
      }
      setLogoutReason(null);
      setServerError(m.login_network_error());
    }
  };

  const submit2fa = async (): Promise<void> => {
    if (!challenge) return;
    setServerError(null);
    setTwofaPending(true);
    try {
      const payload: { challenge_token: string; code?: string; recovery_code?: string } = {
        challenge_token: challenge,
      };
      if (useRecovery) {
        payload.recovery_code = recoveryCode;
      } else {
        payload.code = code2fa;
      }
      const tokens = await verifyLogin2fa(payload);
      setFromLogin(tokens);
      if (tokens.must_reset) {
        await navigate({ to: '/force-password-change' });
      } else {
        await navigate({ to: DEFAULT_LANDING_ROUTE as '/' });
      }
    } catch (err) {
      // С-I3 Р.1, круг 3 гейта (UX, MAJOR): верный код, но доступа нет — та же оговорка и возврат к
      // форме, а не «неверный код».
      if (err instanceof ApiRequestError && err.code === 'auth.access_disabled') {
        setChallenge(null);
        setCode2fa('');
        setRecoveryCode('');
        setUseRecovery(false);
        showAccessDisabled(err);
        return;
      }
      setLogoutReason(null);
      if (err instanceof ApiRequestError) {
        setServerError(err.message || 'Неверный код');
      } else {
        setServerError('Ошибка сети');
      }
    } finally {
      setTwofaPending(false);
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.card} aria-labelledby="login-title">
        <div className={styles.brand} aria-hidden="true">
          <img src="/favicon.svg" alt="" width={24} height={24} className={styles.brandLogo} />
          STAITER
        </div>
        <h1 id="login-title" className={styles.title}>
          {m.login_title()}
        </h1>
        <div role="status" aria-live="polite" aria-atomic="true">
          {logoutReason && <LogoutReasonNotice key={noticeKey} reason={logoutReason} />}
        </div>

        {/* SET-AUDIT-4: 2FA challenge step. */}
        {challenge ? (
          <form
            className={styles.form}
            onSubmit={(e) => {
              e.preventDefault();
              void submit2fa();
            }}
            noValidate
          >
            <p style={{ margin: '0 0 12px 0', fontSize: 14, color: 'var(--text-secondary)' }}>
              {m.ui_vvedite_6_znachnyy_kod_iz_prilozheniya_autentifikatora()}
            </p>
            {useRecovery ? (
              <Input
                label="Резервный код"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
                autoFocus
                autoComplete="one-time-code"
              />
            ) : (
              <Input
                label="Код из приложения"
                inputMode="numeric"
                maxLength={6}
                value={code2fa}
                onChange={(e) => setCode2fa(e.target.value.replace(/\D/g, ''))}
                autoFocus
                autoComplete="one-time-code"
              />
            )}

            {serverError && (
              <p className={styles.serverError} role="alert">
                {serverError}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={twofaPending || (useRecovery ? !recoveryCode : code2fa.length !== 6)}
              loading={twofaPending}
            >
              {m.ui_voyti()}
            </Button>
            <button
              type="button"
              onClick={() => setUseRecovery((v) => !v)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent)',
                cursor: 'pointer',
                marginTop: 8,
                fontSize: 13,
                textAlign: 'left',
              }}
            >
              {useRecovery ? 'Использовать код из приложения' : 'Использовать резервный код'}
            </button>
            <button
              type="button"
              onClick={() => {
                setChallenge(null);
                setCode2fa('');
                setRecoveryCode('');
                setUseRecovery(false);
                setServerError(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                marginTop: 4,
                fontSize: 13,
                textAlign: 'left',
              }}
            >
              {m.ui_otmena()}
            </button>
          </form>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
            <Input
              label={m.login_email_label()}
              type="email"
              autoComplete="email"
              autoFocus
              error={errors.email?.message}
              {...register('email')}
            />
            <Input
              label={m.login_password_label()}
              type="password"
              autoComplete="current-password"
              error={errors.password?.message}
              {...register('password')}
            />

            {/* FE-162 Sprint Q+: Remember email checkbox. */}
            <Checkbox
              label={m.login_remember_me_label()}
              description={m.login_remember_me_hint()}
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />

            {/* Круг 5 гейта Р.1: живой регион постоянный и скрытый — объявляет начало и конец
                блокировки; видимая плашка ниже — без роли, причина выключенной «Войти». */}
            <div role="status" aria-live="polite" aria-atomic="true" className={styles.srOnly}>
              {rateAnnounce}
            </div>
            {rateLimitSeconds !== null && rateUnlockAt && (
              // Круг 4 гейта Р.1 (UI, MINOR): та же геометрия грейда --warn, что у оговорки выше (§ 4.1b),
              // и значок — статус не одним цветом (§ 1.3).
              <p id="login-rate-limit" className={styles.rateLimit}>
                <TriangleAlert size={16} aria-hidden="true" />
                <span>{rateText(rateUnlockAt)}</span>
              </p>
            )}
            {serverError && (
              <p className={styles.serverError} role="alert">
                {serverError}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || rateLimitSeconds !== null}
              loading={isSubmitting}
              aria-describedby={rateLimitSeconds !== null ? 'login-rate-limit' : undefined}
            >
              {m.login_submit()}
            </Button>
          </form>
        )}

        <a className={styles.forgot} href="/auth/password-reset">
          {m.login_forgot_password()}
        </a>
      </section>
    </main>
  );
}

function startCountdown(initial: number, setter: (v: number | null) => void): void {
  let remaining = initial;
  const interval = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      clearInterval(interval);
      setter(null);
    } else {
      setter(remaining);
    }
  }, 1000);
}

function mapLoginError(code: string, fallback: string): string {
  // TODO(S5): центральный error map в lib/i18n (backlog 0.5b).
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return m.login_error_invalid_credentials();
    case 'ACCOUNT_LOCKED':
      return m.login_error_account_locked();
    case 'PASSWORD_CHANGE_REQUIRED':
      return m.login_error_password_change_required();
    default:
      return fallback || m.login_error_generic();
  }
}
